import { useEffect, useState } from "react";
import api from "../lib/api";
import { ListChecks, Plus, Trash2, Loader2, X, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { id: "todo", label: "À faire", color: "#888" },
  { id: "in_progress", label: "En cours", color: "#00D4FF" },
  { id: "done", label: "Terminé", color: "#10B981" },
  { id: "blocked", label: "Bloqué", color: "#EF4444" },
];

const PRIORITY_COLORS = { low: "#888", medium: "#F59E0B", high: "#EF4444" };

export default function TasksBoard() {
  const [tasks, setTasks] = useState([]);
  const [models, setModels] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ model_id: "", priority: "" });
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    try {
      const [t, m] = await Promise.all([api.crmListTasks(), api.crmListModels()]);
      setTasks(t); setModels(m);
      try { const u = await api.authTeam(); setUsers(u); } catch {}
    } catch { toast.error("Erreur"); }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = tasks.filter(t => {
    if (filter.model_id && t.model_id !== filter.model_id) return false;
    if (filter.priority && t.priority !== filter.priority) return false;
    return true;
  });

  const move = async (t, status) => {
    await api.crmUpdateTask(t.id, { status });
    refresh();
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.crmDeleteTask(id); refresh();
  };

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="tasks-board-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// TÂCHES CRM</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Tableau Kanban</h1>
          <p className="text-zinc-400 mt-2">Tâches assignées par modèle/compte. Drag your priorities, ship your day.</p>
        </div>
        <button data-testid="add-task-btn" onClick={() => setShowAdd(true)}
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm glow-volt">
          <Plus className="w-4 h-4" /> Nouvelle tâche
        </button>
      </header>

      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-3 flex gap-3 items-center flex-wrap">
        <span className="text-[10px] font-mono uppercase text-zinc-500 flex items-center gap-1"><Filter className="w-3 h-3" /> FILTRES</span>
        <select data-testid="filter-model" value={filter.model_id} onChange={e => setFilter({...filter, model_id: e.target.value})}
          className="bg-[#070708] border border-[#1E1E24] rounded-sm px-2 py-1 text-xs text-white">
          <option value="">Toutes les modèles</option>
          {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select data-testid="filter-priority" value={filter.priority} onChange={e => setFilter({...filter, priority: e.target.value})}
          className="bg-[#070708] border border-[#1E1E24] rounded-sm px-2 py-1 text-xs text-white">
          <option value="">Toutes priorités</option>
          <option value="high">Haute</option><option value="medium">Moyenne</option><option value="low">Basse</option>
        </select>
        <span className="ml-auto text-[10px] font-mono text-zinc-500">{filtered.length} / {tasks.length}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {STATUSES.map(s => {
          const items = filtered.filter(t => t.status === s.id);
          return (
            <div key={s.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-3" data-testid={`column-${s.id}`}>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#1E1E24]">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-300">{s.label}</div>
                <span className="ml-auto text-[10px] font-mono text-zinc-500">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.length === 0 && <div className="text-[10px] font-mono text-zinc-600 text-center py-4">vide</div>}
                {items.map((t, i) => {
                  const model = models.find(m => m.id === t.model_id);
                  const assignee = users.find(u => u.id === t.assigned_to);
                  return (
                    <div key={t.id} data-testid={`task-${s.id}-${i}`}
                      className="border border-[#1E1E24] bg-[#070708] rounded-sm p-3 hover:border-white/15 transition group">
                      <div className="flex items-start gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white">{t.title}</div>
                          {t.description && <div className="text-[10px] text-zinc-500 mt-1 line-clamp-2">{t.description}</div>}
                        </div>
                        <button onClick={() => remove(t.id)} className="opacity-0 group-hover:opacity-100 transition text-zinc-600 hover:text-red-400">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase">
                        <span className="px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: PRIORITY_COLORS[t.priority] + "30", color: PRIORITY_COLORS[t.priority] }}>{t.priority}</span>
                        {model && <span className="px-1.5 py-0.5 bg-white/5 text-zinc-400 rounded-sm truncate max-w-[80px]">{model.name}</span>}
                        {assignee && <span className="px-1.5 py-0.5 bg-white/5 text-zinc-400 rounded-sm truncate max-w-[60px]">@{assignee.name}</span>}
                        {t.due_date && <span className="px-1.5 py-0.5 bg-white/5 text-zinc-400 rounded-sm">{t.due_date}</span>}
                      </div>
                      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-[#1E1E24]">
                        {STATUSES.filter(x => x.id !== t.status).map(x => (
                          <button key={x.id} onClick={() => move(t, x.id)}
                            className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-[#1E1E24] hover:border-[#CCFF00]/30 hover:text-[#CCFF00] text-zinc-500 rounded-sm transition">
                            → {x.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && <AddTask models={models} users={users} onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); refresh(); }} />}
    </div>
  );
}

function AddTask({ models, users, onClose, onSuccess }) {
  const [f, setF] = useState({ title: "", description: "", model_id: "", assigned_to: "", priority: "medium", due_date: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.crmCreateTask(f); toast.success("Tâche créée"); onSuccess(); }
    catch { toast.error("Erreur"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-md space-y-3" data-testid="add-task-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display text-xl text-white">Nouvelle tâche</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <input data-testid="t-title" required placeholder="Titre" value={f.title} onChange={e => setF({...f, title: e.target.value})}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <textarea data-testid="t-desc" rows={2} placeholder="Description" value={f.description} onChange={e => setF({...f, description: e.target.value})}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="t-model" value={f.model_id} onChange={e => setF({...f, model_id: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="">Modèle (optionnel)</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select data-testid="t-assignee" value={f.assigned_to} onChange={e => setF({...f, assigned_to: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="">Assigné à</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="t-priority" value={f.priority} onChange={e => setF({...f, priority: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="low">Basse</option><option value="medium">Moyenne</option><option value="high">Haute</option>
          </select>
          <input data-testid="t-due" type="date" value={f.due_date} onChange={e => setF({...f, due_date: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
        </div>
        <button disabled={loading} type="submit" data-testid="t-submit"
          className="w-full bg-[#CCFF00] text-black font-medium py-2 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "..." : "Créer"}
        </button>
      </form>
    </div>
  );
}
